// 剪贴板富文本的跨端安全降级。
// HTML 只用于保留常见文字排版，绝不把脚本、事件属性、表单或远程页面
// 控件带到另一台设备；无法识别的标签会被去掉，纯文本始终单独传输。

export const MAX_CLIPBOARD_HTML_LENGTH = 512 * 1024;

const SAFE_TAGS = new Set([
  'a', 'abbr', 'b', 'blockquote', 'br', 'code', 'del', 'div', 'em', 'h1', 'h2',
  'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p', 'pre', 'q', 's', 'small',
  'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead',
  'tr', 'u', 'ul',
]);

const SAFE_ATTRIBUTES = new Set([
  'align', 'alt', 'class', 'colspan', 'dir', 'height', 'href', 'id', 'lang',
  'rel', 'rowspan', 'src', 'style', 'tabindex', 'target', 'title', 'width',
]);

const SAFE_STYLE_PROPERTIES = new Set([
  'background-color', 'border', 'border-bottom', 'border-bottom-color', 'border-bottom-style',
  'border-bottom-width', 'border-collapse', 'border-color', 'border-left', 'border-left-color',
  'border-left-style', 'border-left-width', 'border-right', 'border-right-color',
  'border-right-style', 'border-right-width', 'border-spacing', 'border-style', 'border-top',
  'border-top-color', 'border-top-style', 'border-top-width', 'border-width', 'color', 'direction',
  'font-size', 'font-style', 'font-weight', 'line-height', 'list-style-type', 'margin', 'margin-bottom',
  'margin-left', 'margin-right', 'margin-top', 'opacity', 'padding', 'padding-bottom', 'padding-left',
  'padding-right', 'padding-top', 'text-align', 'text-decoration', 'text-indent', 'vertical-align',
  'white-space', 'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height',
]);

const SAFE_COLOR = /^(?:#[0-9a-f]{3,8}|transparent|currentcolor|black|silver|gray|white|maroon|red|purple|fuchsia|green|lime|olive|yellow|navy|blue|teal|aqua|orange|aliceblue|rebeccapurple|rgba?\(\s*[\d.%\s]+(?:[,/]\s*[\d.%\s]+){2,3}\)|hsla?\(\s*[\d.%\s]+(?:[,/]\s*[\d.%\s]+){2,3}\))$/i;
const SAFE_LENGTH = /^(?:0|(?:\d+(?:\.\d+)?|\.\d+)(?:px|pt|pc|em|rem|ex|ch|vw|vh|vmin|vmax|%|in|cm|mm))$/i;
const SAFE_BORDER_STYLE = /^(?:none|hidden|dotted|dashed|solid|double|groove|ridge|inset|outset)$/i;
const SAFE_TEXT_ALIGN = /^(?:left|right|center|justify|start|end)$/i;
const SAFE_VERTICAL_ALIGN = /^(?:top|middle|bottom|baseline|sub|super|text-top|text-bottom)$/i;
const SAFE_TEXT_DECORATION = /^(?:none|underline|overline|line-through)$/i;
const SAFE_WHITE_SPACE = /^(?:normal|nowrap|pre|pre-wrap|pre-line|break-spaces)$/i;
const SAFE_LIST_STYLE = /^(?:none|disc|circle|square|decimal|decimal-leading-zero|lower-alpha|upper-alpha|lower-roman|upper-roman)$/i;

function safeLengthList(value: string, allowAuto = false, allowNegative = false): boolean {
  const tokens = value.trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 1 || tokens.length > 4) return false;
  return tokens.every((token) => {
    if (allowAuto && token.toLowerCase() === 'auto') return true;
    if (SAFE_LENGTH.test(token)) return true;
    if (allowNegative && /^-(?:\d+(?:\.\d+)?|\.\d+)(?:px|pt|pc|em|rem|ex|ch|vw|vh|vmin|vmax|%|in|cm|mm)$/i.test(token)) return true;
    return false;
  });
}

function safeBorder(value: string): boolean {
  const tokens = value.trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 1 || tokens.length > 3) return false;
  let style = false;
  let width = false;
  let color = false;
  for (const token of tokens) {
    if (!width && (SAFE_LENGTH.test(token) || /^(?:thin|medium|thick)$/i.test(token))) {
      width = true;
    } else if (!style && SAFE_BORDER_STYLE.test(token)) {
      style = true;
    } else if (!color && SAFE_COLOR.test(token)) {
      color = true;
    } else {
      return false;
    }
  }
  return true;
}

function safeStyleValue(property: string, rawValue: string): boolean {
  const value = rawValue.trim().replace(/\s+/g, ' ');
  if (!value || value.length > 256 || /[{}<>]|url\s*\(|expression\s*\(|javascript:|vbscript:|data:/i.test(value)) return false;
  if (property === 'color' || property === 'background-color' || property.endsWith('-color')) return SAFE_COLOR.test(value);
  if (property === 'border' || /^border-(?:top|right|bottom|left)$/.test(property)) return safeBorder(value);
  if (property === 'border-collapse') return /^(?:collapse|separate)$/i.test(value);
  if (property === 'border-spacing') return safeLengthList(value);
  if (property === 'border-style' || /^border-(?:top|right|bottom|left)-style$/.test(property)) {
    return value.split(/\s+/).every((token) => SAFE_BORDER_STYLE.test(token));
  }
  if (property === 'border-width' || /^border-(?:top|right|bottom|left)-width$/.test(property)) return safeLengthList(value);
  if (property === 'margin') return safeLengthList(value, true, true);
  if (property.startsWith('margin-')) return safeLengthList(value, true, true) && value.split(/\s+/).length === 1;
  if (property === 'padding') return safeLengthList(value);
  if (property.startsWith('padding-')) return safeLengthList(value) && value.split(/\s+/).length === 1;
  if (['width', 'height', 'min-width', 'min-height', 'max-width', 'max-height'].includes(property)) {
    return /^(?:auto|fit-content|max-content|min-content)$/i.test(value) || safeLengthList(value) && value.split(/\s+/).length === 1;
  }
  if (property === 'font-size') return /^(?:xx-small|x-small|small|medium|large|x-large|xx-large|smaller|larger)$/i.test(value) || safeLengthList(value) && value.split(/\s+/).length === 1;
  if (property === 'line-height') return /^(?:normal|\d+(?:\.\d+)?)$/i.test(value) || safeLengthList(value) && value.split(/\s+/).length === 1;
  if (property === 'font-style') return /^(?:normal|italic|oblique)$/i.test(value);
  if (property === 'font-weight') return /^(?:normal|bold|bolder|lighter|[1-9]00)$/i.test(value);
  if (property === 'text-align') return SAFE_TEXT_ALIGN.test(value);
  if (property === 'text-decoration') return SAFE_TEXT_DECORATION.test(value);
  if (property === 'text-indent') return safeLengthList(value) && value.split(/\s+/).length === 1;
  if (property === 'vertical-align') return SAFE_VERTICAL_ALIGN.test(value) || safeLengthList(value) && value.split(/\s+/).length === 1;
  if (property === 'white-space') return SAFE_WHITE_SPACE.test(value);
  if (property === 'direction') return /^(?:ltr|rtl)$/i.test(value);
  if (property === 'opacity') return /^(?:0|1|0?\.\d+)$/.test(value);
  if (property === 'list-style-type') return SAFE_LIST_STYLE.test(value);
  return false;
}

function sanitizeInlineStyle(value: string): string {
  if (value.length > 4096) return '';
  const result: string[] = [];
  const seen = new Set<string>();
  for (const declaration of value.split(';')) {
    const separator = declaration.indexOf(':');
    if (separator <= 0 || result.length >= 32) continue;
    const property = declaration.slice(0, separator).trim().toLowerCase();
    if (!SAFE_STYLE_PROPERTIES.has(property) || seen.has(property)) continue;
    const propertyValue = declaration.slice(separator + 1);
    if (!safeStyleValue(property, propertyValue)) continue;
    seen.add(property);
    result.push(`${property}: ${propertyValue.trim().replace(/\s+/g, ' ')}`);
  }
  return result.join('; ');
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function safeUrl(value: string, attribute: 'href' | 'src'): boolean {
  const normalized = value.trim().toLowerCase();
  // Browsers ignore ASCII control/space characters while interpreting a URL.
  // Check the compact form as well, otherwise `java\nscript:` could become an
  // executable link after the sanitized HTML is pasted into another app.
  const compact = normalized.replace(/[\u0000-\u0020]+/g, '');
  if (!compact) return false;
  if (compact.startsWith('javascript:') || compact.startsWith('vbscript:')) return false;
  if (attribute === 'src' && compact.startsWith('data:') && !/^data:image\/(?:png|jpeg|gif|webp);base64,/.test(compact)) {
    return false;
  }
  if (compact.startsWith('data:')) return attribute === 'src';
  return /^(?:https?:|mailto:|tel:|#|\/|\.\.?\/)/.test(compact) || !/^[a-z][a-z0-9+.-]*:/.test(compact);
}

function sanitizeAttributes(raw: string, tagName: string): string {
  const attributes: string[] = [];
  const pattern = /([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(raw))) {
    const name = match[1].toLowerCase();
    if (!SAFE_ATTRIBUTES.has(name) || name.startsWith('on')) continue;
    const value = match[2] ?? match[3] ?? match[4];
    if (value === undefined) {
      if (name === 'tabindex' || name === 'style') continue;
      attributes.push(` ${name}`);
      continue;
    }
    const trimmed = value.trim();
    if (name === 'style') {
      const safeStyle = sanitizeInlineStyle(trimmed);
      if (safeStyle) attributes.push(` style="${escapeAttribute(safeStyle)}"`);
      continue;
    }
    if ((name === 'href' || name === 'src') && !safeUrl(trimmed, name)) continue;
    // Inline images preserve pasted screenshots without allowing a pasted
    // document to trigger a network request or leak a referrer on paste.
    if (tagName === 'img' && name === 'src' && !/^data:image\/(?:png|jpeg|gif|webp);base64,/i.test(trimmed)) continue;
    if (name === 'target' && !/^_(?:blank|self|parent|top)$/i.test(trimmed)) continue;
    if (name === 'rel' && !/^[a-z\s-]{0,80}$/i.test(trimmed)) continue;
    if (['class', 'id', 'lang', 'dir', 'align'].includes(name) && !/^[\w\s:.-]{0,120}$/u.test(trimmed)) continue;
    if (['width', 'height', 'colspan', 'rowspan', 'tabindex'].includes(name) && !/^\d{1,5}$/.test(trimmed)) continue;
    attributes.push(` ${name}="${escapeAttribute(trimmed)}"`);
  }
  return attributes.join('');
}

/**
 * 清理并限制剪贴板 HTML。该函数不负责把 HTML 转成纯文本，调用方必须
 * 同时保留系统/浏览器提供的 text/plain 版本作为无格式回退。
 */
export function sanitizeClipboardHtml(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return '';
  let html = value.slice(0, MAX_CLIPBOARD_HTML_LENGTH);
  // 先移除可包含任意脚本/外部控件的整段内容；没有闭合标签时从开头
  // 一并舍弃到末尾，避免恶意内容借不完整 HTML 穿透后续标签过滤。
  html = html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<\s*(script|style|iframe|object|embed|svg|math|template|form|noscript|textarea|title)\b[^>]*>[\s\S]*?(?:<\s*\/\s*\1\s*>|$)/gi, '')
    // meta/link/base are normally void elements. Treating them as paired
    // blocks would erase the rest of otherwise valid HTML when a clipboard
    // producer puts `<meta charset=...>` before the actual content.
    .replace(/<\s*\/?\s*(?:link|meta|base)\b[^>]*>/gi, '')
    .replace(/<![^>]*>/g, '')
    .replace(/<\?[\s\S]*?\?>/g, '');

  html = html.replace(/<\s*(\/?)\s*([a-z][a-z0-9:-]*)([^>]*)>/gi, (_whole, closing: string, rawName: string, rawAttributes: string) => {
    const name = rawName.toLowerCase();
    if (!SAFE_TAGS.has(name)) return '';
    if (closing) return `</${name}>`;
    const selfClosing = /\/\s*$/.test(rawAttributes);
    const attributes = sanitizeAttributes(rawAttributes.replace(/\/\s*$/, ''), name);
    return `<${name}${attributes}${selfClosing || name === 'br' || name === 'hr' || name === 'img' ? ' /' : ''}>`;
  });
  return html.slice(0, MAX_CLIPBOARD_HTML_LENGTH);
}
